import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../api';

export type TelegramStep = 'idle' | 'generating' | 'waiting' | 'success' | 'timeout' | 'error';

export interface TelegramLinkData {
  link: string;
  pin: string;
}

const POLL_INTERVAL_MS = 2000;
const POLL_TIMEOUT_MS = 10 * 60 * 1000; // 10 minutes

export function useTelegramLink(onSuccess?: () => void) {
  const { user, refreshUser } = useAuth();
  const [step, setStep] = useState<TelegramStep>('idle');
  const [linkData, setLinkData] = useState<TelegramLinkData | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollStartRef = useRef<number>(0);
  // Use a ref so the setInterval callback always reads the latest value
  // without needing to re-create the interval on every state change
  const isWaitingRef = useRef(false);
  const generation = useRef(0);
  const pendingLink = useRef<AbortController | null>(null);
  const onSuccessRef = useRef(onSuccess);
  onSuccessRef.current = onSuccess;

  const stopPolling = useCallback(() => {
    generation.current += 1;
    pendingLink.current?.abort();
    pendingLink.current = null;
    if (intervalRef.current !== null) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    isWaitingRef.current = false;
  }, []);

  // React to telegram_id appearing in user context (triggered by refreshUser in polling)
  useEffect(() => {
    if (isWaitingRef.current && user?.telegram_id) {
      stopPolling();
      setStep('success');
      onSuccessRef.current?.();
    }
  }, [user?.telegram_id, step, stopPolling]);

  // Cleanup interval on unmount to prevent memory leaks
  useEffect(() => () => stopPolling(), [stopPolling]);

  const startLink = useCallback(async () => {
    stopPolling();
    const currentGeneration = generation.current;
    const controller = new AbortController();
    pendingLink.current = controller;
    setStep('generating');
    setLinkData(null);
    try {
      const { data } = await api.post<TelegramLinkData>('/auth/telegram/generate-link', undefined, { signal: controller.signal });
      if (generation.current !== currentGeneration || controller.signal.aborted) return;
      pendingLink.current = null;
      setLinkData(data);
      setStep('waiting');
      isWaitingRef.current = true;
      pollStartRef.current = Date.now();
      let refreshing = false;
      intervalRef.current = setInterval(async () => {
        if (generation.current !== currentGeneration) return;
        if (Date.now() - pollStartRef.current > POLL_TIMEOUT_MS) {
          stopPolling();
          setStep('timeout');
          return;
        }
        if (refreshing) return;
        refreshing = true;
        try {
          await refreshUser();
        } catch {
          // Silently ignore polling errors — network hiccup shouldn't abort the flow
        } finally {
          refreshing = false;
        }
      }, POLL_INTERVAL_MS);
    } catch {
      if (generation.current === currentGeneration && !controller.signal.aborted) setStep('error');
    }
  }, [stopPolling, refreshUser]);

  const reset = useCallback(() => {
    stopPolling();
    setStep('idle');
    setLinkData(null);
  }, [stopPolling]);

  return { step, linkData, startLink, reset, stopPolling };
}
